import React from 'react';
import { motion } from 'framer-motion';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  className?: string;
  variant?: 'rectangular' | 'circular' | 'text';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '1rem',
  className = '',
  variant = 'rectangular'
}) => {
  const baseClasses = 'bg-gradient-to-r from-surface/50 via-surface/80 to-surface/50 animate-pulse';

  const variantClasses = {
    rectangular: 'rounded',
    circular: 'rounded-full',
    text: 'rounded-sm'
  };

  const styles = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={styles}
    />
  );
};

interface TeamCardSkeletonProps {
  count?: number;
}

export const TeamCardSkeleton: React.FC<TeamCardSkeletonProps> = ({ count = 6 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="glass-card p-6"
        >
          {/* Team Logo */}
          <div className="flex items-center space-x-4 mb-4">
            <Skeleton variant="circular" width={48} height={48} />
            <div className="flex-1">
              <Skeleton height="1.25rem" className="mb-2" width="70%" />
              <Skeleton height="0.875rem" width="50%" />
            </div>
          </div>

          {/* Team Stats */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <Skeleton width="40%" height="0.875rem" />
              <Skeleton width="20%" height="0.875rem" />
            </div>
            <div className="flex justify-between">
              <Skeleton width="50%" height="0.875rem" />
              <Skeleton width="30%" height="0.875rem" />
            </div>
            <div className="flex justify-between">
              <Skeleton width="35%" height="0.875rem" />
              <Skeleton width="25%" height="0.875rem" />
            </div>
          </div>

          {/* Action Button */}
          <div className="mt-6">
            <Skeleton height="2.5rem" className="rounded-lg" />
          </div>
        </motion.div>
      ))}
    </div>
  );
};

interface PlayerCardSkeletonProps {
  count?: number;
}

export const PlayerCardSkeleton: React.FC<PlayerCardSkeletonProps> = ({ count = 8 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="glass-card p-6"
        >
          {/* Player Avatar */}
          <div className="flex flex-col items-center mb-4">
            <Skeleton variant="circular" width={64} height={64} className="mb-3" />
            <Skeleton height="1.25rem" width="80%" className="mb-1" />
            <Skeleton height="0.875rem" width="60%" />
          </div>

          {/* Player Info */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Skeleton width="40%" height="0.75rem" />
              <Skeleton width="30%" height="0.75rem" />
            </div>
            <div className="flex justify-between">
              <Skeleton width="50%" height="0.75rem" />
              <Skeleton width="20%" height="0.75rem" />
            </div>
            <div className="flex justify-between">
              <Skeleton width="35%" height="0.75rem" />
              <Skeleton width="40%" height="0.75rem" />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

interface GameCardSkeletonProps {
  count?: number;
}

export const GameCardSkeleton: React.FC<GameCardSkeletonProps> = ({ count = 4 }) => {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="glass-card p-6"
        >
          <div className="flex items-center justify-between">
            {/* Teams */}
            <div className="flex items-center space-x-8">
              {/* Home Team */}
              <div className="flex items-center space-x-3">
                <Skeleton variant="circular" width={40} height={40} />
                <div>
                  <Skeleton height="1rem" width="80px" className="mb-1" />
                  <Skeleton height="0.75rem" width="60px" />
                </div>
              </div>

              {/* VS */}
              <Skeleton height="0.875rem" width="30px" />

              {/* Away Team */}
              <div className="flex items-center space-x-3">
                <Skeleton variant="circular" width={40} height={40} />
                <div>
                  <Skeleton height="1rem" width="80px" className="mb-1" />
                  <Skeleton height="0.75rem" width="60px" />
                </div>
              </div>
            </div>

            {/* Score/Time */}
            <div className="text-right">
              <Skeleton height="1.5rem" width="60px" className="mb-1" />
              <Skeleton height="0.75rem" width="80px" />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

interface ListSkeletonProps {
  count?: number;
  height?: number;
  showAvatar?: boolean;
}

export const ListSkeleton: React.FC<ListSkeletonProps> = ({
  count = 5,
  height = 60,
  showAvatar = true
}) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
          className="glass-card p-4"
        >
          <div className="flex items-center space-x-4">
            {showAvatar && <Skeleton variant="circular" width={40} height={40} />}
            <div className="flex-1">
              <Skeleton height="1rem" className="mb-2" width="60%" />
              <Skeleton height="0.75rem" width="40%" />
            </div>
            <Skeleton width="80px" height="0.875rem" />
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default Skeleton;